'use client';

import { getSiteIdForLocale } from '@/helpers/getSiteId';
import {
    type CustomerData,
    addItemAtom,
    availableShippingMethodsAtom,
    cartItemsAtom,
    cartNumberAtom,
    clearCartAtom,
    createOrderAtom,
    customerDataAtom,
    errorAtom,
    initializeCartAtom,
    isInitializedAtom,
    isLoadingAtom,
    removeItemAtom,
    selectShippingMethodAtom,
    selectedShippingMethodAtom,
    shippingCostAtom,
    subtotalAtom,
    totalAtom,
    updateCustomerAtom,
} from '@/lib/atoms/cart-atoms';
import { store } from '@/lib/store';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useLocale } from 'next-intl';
import { type ReactNode, useEffect } from 'react';

/**
 * JotaiCartProvider initializes cart state using Jotai atoms
 * This component should be placed high in the component tree
 */
export function JotaiCartProvider({ children }: { children: ReactNode }) {
    // Get the initialization atom and current locale
    const initialize = useSetAtom(initializeCartAtom, { store });
    const isInitialized = useAtomValue(isInitializedAtom, { store });
    const locale = useLocale();
    const siteId = Number.parseInt(getSiteIdForLocale(locale), 10);

    // Only initialize cart on mount if there's a stored cart number
    useEffect(() => {
        if (!isInitialized) {
            // Check if there's a stored cart number
            const storedCartNumber = localStorage.getItem('cartNumber');
            if (storedCartNumber && storedCartNumber !== 'null') {
                // Initialize with stored cart
                initialize({ siteId });
            } else {
                // Just mark as initialized without API call
                initialize({ siteId });
            }
        }
    }, [initialize, isInitialized, siteId]);

    return <>{children}</>;
}

/**
 * Hook to access cart state and actions
 * Can be used anywhere in the application after JotaiCartProvider is mounted
 */
export function useJotaiCart() {
    // Get current locale and convert to siteId
    const locale = useLocale();
    const siteId = Number.parseInt(getSiteIdForLocale(locale), 10);

    // State atoms
    const [items] = useAtom(cartItemsAtom, { store });
    const [cartNumber] = useAtom(cartNumberAtom, { store });
    const [customerData] = useAtom(customerDataAtom, { store });
    const [availableShippingMethods] = useAtom(availableShippingMethodsAtom, { store });
    const [selectedShippingMethod] = useAtom(selectedShippingMethodAtom, { store });
    const [shippingCost] = useAtom(shippingCostAtom, { store });
    const [subtotal] = useAtom(subtotalAtom, { store });
    const total = useAtomValue(totalAtom, { store });
    const [isInitialized] = useAtom(isInitializedAtom, { store });
    const [isLoading] = useAtom(isLoadingAtom, { store });
    const [error] = useAtom(errorAtom, { store });

    // Action atoms
    const addItem = useSetAtom(addItemAtom, { store });
    const removeItem = useSetAtom(removeItemAtom, { store });
    const updateCustomer = useSetAtom(updateCustomerAtom, { store });
    const selectShippingMethod = useSetAtom(selectShippingMethodAtom, { store });
    const clearCart = useSetAtom(clearCartAtom, { store });
    const createOrder = useSetAtom(createOrderAtom, { store });
    const initialize = useSetAtom(initializeCartAtom, { store });

    // Wrap the action atoms with the expected function signatures
    const addItemToCart = async (purchasableId: number, quantity: number) => {
        return addItem({ purchasableId, quantity, siteId });
    };

    const removeItemFromCart = async (purchasableId: number, quantity?: number) => {
        return removeItem({ purchasableId, quantity, siteId });
    };

    const updateCustomerData = async (data: CustomerData) => {
        return updateCustomer({ ...data, siteId });
    };

    const selectShippingMethodAction = async (methodName: string) => {
        return selectShippingMethod({ methodName, siteId });
    };

    const clearCartAction = () => {
        clearCart(siteId);
    };

    const createOrderAction = async (returnUrl: string, cancelUrl: string) => {
        return createOrder({ returnUrl, cancelUrl, siteId });
    };

    // Force initialization when needed (e.g., on cart/checkout pages)
    const forceInitialize = async () => {
        return initialize({ siteId, force: true });
    };

    return {
        items,
        total,
        subtotal,
        shippingCost,
        cartNumber,
        customerData,
        availableShippingMethods,
        selectedShippingMethod,
        addItem: addItemToCart,
        removeItem: removeItemFromCart,
        updateCustomer: updateCustomerData,
        selectShippingMethod: selectShippingMethodAction,
        clearCart: clearCartAction,
        createOrder: createOrderAction,
        forceInitialize,
        isInitialized,
        isLoading,
        error,
    };
}
